import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CalendarDays, Clock, Phone, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
];

interface BookConsultationDialogProps {
  trigger: React.ReactNode;
}

const BookConsultationDialog = ({ trigger }: BookConsultationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !time || !name.trim() || !email.trim()) return;
    setSubmitted(true);
  };

  const handleReset = () => {
    setSubmitted(false);
    setDate(undefined);
    setTime("");
    setName("");
    setEmail("");
    setPhone("");
    setMessage("");
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) handleReset();
  };

  const isPastDate = (day: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return day < today;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="h-14 w-14 text-primary mx-auto mb-4" />
            <DialogHeader>
              <DialogTitle className="text-2xl heading-luxury text-foreground mb-3">
                Consultation Requested
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground mb-1">
              Thank you, <span className="text-foreground font-medium">{name}</span>!
            </p>
            <p className="text-muted-foreground mb-4">
              Your consultation is requested for{" "}
              <span className="text-foreground font-medium">
                {date?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </span>{" "}
              at <span className="text-foreground font-medium">{time}</span>.
            </p>
            <p className="text-muted-foreground text-sm">
              Gurbir will reach out shortly to confirm.
            </p>
            <Button className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl heading-luxury text-foreground">
                Book a Consultation
              </DialogTitle>
              <p className="text-muted-foreground text-sm">
                Schedule a free consultation with Gurbir Samran to discuss your farm & land goals.
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                {/* Left: Calendar & Time */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">Select a Date</span>
                  </div>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(day) => isPastDate(day) || day.getDay() === 0}
                    className={cn("p-3 pointer-events-auto rounded-md border border-border")}
                  />
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium text-foreground">Select a Time</span>
                    </div>
                    <Select value={time} onValueChange={setTime}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Right: Form */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Full Name *</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" required maxLength={100} />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Email *</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required maxLength={255} />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Phone</label>
                    <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" maxLength={20} />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">What would you like to discuss?</label>
                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us about your farm & land interests..." rows={3} maxLength={1000} />
                  </div>

                  {date && time && (
                    <div className="bg-muted rounded-md p-3 text-sm">
                      <p className="text-foreground font-medium">
                        {date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {time}
                      </p>
                      <p className="text-muted-foreground">with Gurbir Samran</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={!date || !time || !name.trim() || !email.trim()}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-5 font-semibold gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    Request Consultation
                  </Button>
                  <p className="text-muted-foreground text-xs text-center">
                    Free · No obligation · Confirmed within 24 hours
                  </p>
                </div>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookConsultationDialog;
